import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { Reveal } from "@/components/site/Reveal";
import { RichText } from "@/components/site/RichText";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQ = [
  {
    cat: "Comprendre",
    q: "Quelle différence entre un sculpteur classique et un sculpteur contemporain ?",
    a: "La différence tient moins au style qu'à la liberté : le sculpteur classique répond à des codes (sujet noble, marbre ou bronze, commande officielle), tandis que le contemporain choisit librement son sujet, son matériau — du bronze à l'objet récupéré — et sa destination. « Contemporain » désigne d'abord une époque : celle des artistes vivants.",
  },
  {
    cat: "Comprendre",
    q: "La sculpture contemporaine se limite-t-elle à l'abstrait ?",
    a: "Non. La figuration est très présente dans la création actuelle : bustes, corps, animaux réinterprétés cohabitent avec l'abstraction, la pop sculpture ou l'art urbain. Les frontières entre courants sont d'ailleurs poreuses.",
  },
  {
    cat: "Comprendre",
    q: "Comment se forme un sculpteur aujourd'hui ?",
    a: "Par les écoles supérieures d'art, les ateliers de compagnonnage, l'apprentissage auprès de fondeurs ou de tailleurs de pierre — ou en autodidacte. Il n'existe plus de voie unique : la légitimité vient de l'œuvre et de sa présence sur la scène artistique.",
  },
  {
    cat: "Éditions & valeur",
    q: "Qu'est-ce qu'une épreuve d'artiste (EA) ?",
    a: "Un exemplaire supplémentaire produit en plus du tirage numéroté, marqué « EA ». En droit français, une série d'originaux ne peut dépasser douze exemplaires : huit numérotés et quatre EA. Une épreuve d'artiste a la même valeur intrinsèque qu'un exemplaire numéroté.",
  },
  {
    cat: "Éditions & valeur",
    q: "Comment est fixé le prix d'une sculpture ?",
    a: "Selon la taille, le matériau (le bronze coûte plus cher à produire que la résine), le nombre d'exemplaires et la notoriété de l'artiste. Pour les artistes cotés, les résultats de ventes aux enchères servent de référence ; pour les émergents, le prix reflète le temps de travail et les coûts de production.",
  },
  {
    cat: "Éditions & valeur",
    q: "La sculpture contemporaine est-elle un investissement ?",
    a: "Elle peut prendre de la valeur, mais rien n'est garanti : le marché de l'art comporte des risques. La meilleure approche reste d'acheter une œuvre qu'on aime et dont on vérifiera l'authenticité — le plaisir quotidien est le seul rendement certain.",
  },
  {
    cat: "Acheter",
    q: "Comment vérifier l'authenticité d'une sculpture ?",
    a: "Exiger le certificat d'authenticité signé par l'artiste (titre, année, matériaux, dimensions, numéro de tirage), contrôler la signature ou le poinçon sur l'œuvre, et vérifier la cohérence avec le catalogue de l'artiste. En cas de doute sur une œuvre de marché, un expert peut être consulté.",
  },
  {
    cat: "Acheter",
    q: "Acheter en direct est-il moins cher qu'en galerie ?",
    a: "Souvent, oui : le prix d'atelier n'intègre pas la commission de galerie, qui peut représenter 30 à 50 % du prix public. En contrepartie, la galerie apporte sélection, conseil et réseau. Les deux voies sont complémentaires.",
  },
  {
    cat: "Acheter",
    q: "Peut-on commander une sculpture sur mesure ?",
    a: "Beaucoup de sculpteurs acceptent la commande : portrait, sujet particulier, format adapté à un lieu. Délais, étapes de validation (esquisse, maquette) et échéancier de paiement se définissent ensemble, par écrit.",
  },
  {
    cat: "Vivre avec",
    q: "Peut-on exposer une sculpture en extérieur ?",
    a: "Cela dépend du matériau : bronze, acier corten et certaines pierres résistent très bien aux intempéries ; résine, plâtre et terre cuite sont à réserver à l'intérieur. Demandez toujours à l'artiste ou au vendeur la compatibilité de l'œuvre avec une exposition extérieure.",
  },
  {
    cat: "Vivre avec",
    q: "Comment entretenir une sculpture en bronze ?",
    a: "Le bronze ne demande presque rien : un dépoussiérage doux et, une fois l'an, une fine couche de cire microcristalline pour protéger la patine. Éviter les produits abrasifs et les chocs thermiques, qui altéreraient la couleur de surface.",
  },
  {
    cat: "Vivre avec",
    q: "Comment se passe la livraison d'une œuvre lourde ou fragile ?",
    a: "Elle se prévoit par écrit avant l'achat : emballage adapté (caisse sur mesure pour les pièces fragiles), transporteur spécialisé en œuvres d'art, assurance ad valorem, et conditions d'installation. Pour un bronze lourd, prévoyez l'accès et le support porteur.",
  },
];

const CATS = [...new Set(FAQ.map((f) => f.cat))];

export default function FaqPage() {
  useSEO(
    "Foire Aux Questions — Sculpture Contemporaine & Acquisition",
    "Réponses claires et pédagogiques aux questions fréquentes sur la conservation, la valeur des épreuves d'artiste, la commande sur-mesure et la livraison d'œuvres.",
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      inLanguage: "fr-FR",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    }
  );

  return (
    <main data-testid="faq-page" className="bg-paper pb-24 pt-28 sm:pt-36">
      <header className="mx-auto max-w-7xl px-6 lg:px-10">
        <Reveal y={16}>
          <p className="eyebrow">Questions fréquentes</p>
        </Reveal>
        <h1 className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl lg:text-6xl">
          Tout ce que l'on se demande avant d'entrer dans le sujet
        </h1>
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-2xl text-lg font-light leading-relaxed text-smoke">
            Comprendre, acheter, vivre avec une œuvre : les réponses claires
            aux questions les plus fréquentes sur la sculpture contemporaine.
          </p>
        </Reveal>
      </header>

      <div className="mx-auto mt-16 max-w-4xl px-6 lg:px-10">
        {CATS.map((cat) => (
          <section key={cat} className="mt-14 first:mt-0" data-testid={`faq-cat-${cat.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <h2 className="eyebrow">{cat}</h2>
            <Accordion type="single" collapsible className="mt-5 border-t border-ink/10">
              {FAQ.filter((f) => f.cat === cat).map((f) => (
                <AccordionItem key={f.q} value={f.q} className="border-b border-ink/10">
                  <AccordionTrigger
                    data-testid={`faq-q-${f.q.slice(0, 24).toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="py-6 text-left font-serif text-xl text-ink transition-colors hover:text-bronze hover:no-underline sm:text-2xl"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-base font-light leading-relaxed text-smoke">
                    <RichText text={f.a} />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        ))}

        <Reveal className="mt-16 border border-ink/15 bg-cream p-10 text-center" >
          <p className="font-serif text-2xl text-ink sm:text-3xl">Une question reste sans réponse ?</p>
          <p className="mt-3 text-sm font-light text-smoke">
            Ce site est une ressource vivante : vos questions nourrissent ses contenus.
          </p>
          <Link
            to="/contact"
            data-testid="faq-cta-contact"
            className="group mt-6 inline-flex items-center gap-3 bg-ink px-7 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-paper transition-colors duration-300 hover:bg-bronze"
          >
            Poser ma question
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </main>
  );
}
